import React from "react";
import { useQueryClient } from "react-query";
import _ from "underscore";
import NumberFormat from "react-number-format";
import { omit, filter } from "lodash";
import { useExchangeRateConversion } from "../services/currencies";
import { Icon } from "@material-ui/core";
import { useUnitsOfMeasure } from "../services/settings";

const getConvertedAmount = (amount, usgFactor) => {
  let factor = usgFactor ? usgFactor : 1;
  let cagAmount = amount * (1 / factor);

  const convertedValue = parseFloat(cagAmount.toFixed(10));
  return isNaN(convertedValue) ? 0 : convertedValue;
};
const ConvertedNumber = (props) => {
  //list of props allowed from parent
  const filteredProps = omit(
    props,
    filter(
      Object.keys(props),
      (key) =>
        [
          "isNumericString",
          "thousandSeparator",
          "decimalSeparator",
          "thousandsGroupStyle",
          "decimalScale",
          "fixedDecimalScale",
          "format",
          "prefix",
          "suffix",
          "style",
        ].indexOf(key) < 0
    )
  );

  const controlProps = {
    isNumericString: false,
    thousandSeparator: ",",
    decimalSeparator: ".",
    thousandsGroupStyle: "thousand",
    decimalScale: undefined, //number of decimal places
    fixedDecimalScale: false, // show the trailing zeroes
    type: "text",
    displayType: "text",
    format: undefined, //can use # where a number is to be entered.
    prefix: undefined,
    suffix: undefined,
    ...filteredProps,
  };

  const queryClient = useQueryClient();
  const conversion = useExchangeRateConversion({
    queryClient,
    extraKeys: [
      props.rateProviderCode || "SYSTEM",
      props.fromCurrencyCode || "BASE",
      props.toCurrencyCode || "BASE",
      props.rateDate || "0001-01-01",
    ],
  });
  const uomRecords = useUnitsOfMeasure({
    queryClient,
    useCache: true,
    extraKeys: [],
  });
  const uomRecord = _.findWhere(uomRecords ? uomRecords.data : [], {
    uomCode: props.uom,
    toUomCode: props.toUom || "USG",
  });

  if (props.dontConvert && !props.showError === true && !props.highlightError === true) {
    return (
      <div>
        <NumberFormat
          {...controlProps}
          value={
            props.convertedValue !== undefined
              ? props.convertedValue
              : props.value
          }
        />
      </div>
    );
  }

  if (!props.dontConvert && conversion.isLoading) {
    if (props.convertedValue === undefined) {
      return <div>Converting ...</div>;
    } else {
      return (
        <div>
          <NumberFormat {...controlProps} value={props.convertedValue} />
        </div>
      );
    }
  }
  if (
    !props.dontConvert &&
    (conversion.error || (conversion.data && conversion.data.errorCode))
  ) {
    return (
      <div>
        {conversion.data && conversion.data.errorCode
          ? conversion.data.errorText
          : "Error ..."}
      </div>
    );
  }

  if (typeof props.onError === 'function' && (conversion.error || (conversion.data && conversion.data.errorCode))) {
    props.onError(conversion.data.errorText || 'An exchange rate conversion error has occurred', conversion.data.errorCode);
  }

  let error = <div></div>;
  let convertedValue = conversion.data
    ? (props.value * conversion.data.conversionFactor).toFixed(
        controlProps.decimalScale
      )
    : props.value;
  //  let convertedValue = (props.value * conversion.data.conversionFactor).toFixed(controlProps.decimalScale);
  if (props.uom && uomRecord) {
    convertedValue = getConvertedAmount(convertedValue, uomRecord.factor);
  }
  if (isNaN(convertedValue)) convertedValue = 0;
  if (!controlProps.isNumericString)
    convertedValue = parseFloat(convertedValue);
  if ((props.onChange || props.showError || props.highlightError) &&
    (!convertedValue?.toFixed || !props.convertedValue?.toFixed || convertedValue.toFixed(controlProps.decimalScale) !==
      props.convertedValue.toFixed(controlProps.decimalScale)) 
  ) {
    if (props.highlightError) {
      let errorStyle = {color: "#FF0000"};
      if (props.highlightError === "string") {
        errorStyle = {color: props.highlightError};
      }
      if (props.highlightError === "object") {
        errorStyle = props.highlightError;
      }
      controlProps['style'] = {...controlProps.style, ...errorStyle}
    }
    if (typeof props.onError === 'function' && !conversion.isLoading ) {
      props.onError('Converted value does not match displayed value');
    }
    if (props.showError === true) {
      error = (
        <Icon
          style={{ fontSize: 12, marginTop: 0, position: "absolute" }}
          color="error"
        >
          error
        </Icon>
      );
    }
    if (!props.dontConvert && props.onChange) {
      setTimeout(
        () =>
          props.onChange(
            parseFloat(convertedValue.toFixed(controlProps.decimalScale))
          ),
        1
      );
    }
  }

  if (props.dontConvert) {
    return (
      <div>
        <NumberFormat
          {...controlProps}
          value={
            props.convertedValue !== undefined
              ? props.convertedValue
              : props.value
          }
        />
        {error}
      </div>
    );
  }

  return (
    <div>
      <NumberFormat {...controlProps} value={convertedValue} />
      {error}
    </div>
  );
};

export default ConvertedNumber;
